import { z } from 'zod';
import { CLIENT_ROLES, OPERATOR_ROLES, ROLES } from '../roles.js';
import { code, displayName, email, entityStatus, pageQuery, timeZone, uuid } from './common.js';
// ---- Sites ----
export const address = z.object({
    line1: z.string().trim().min(1).max(120),
    line2: z.string().trim().max(120).optional(),
    city: z.string().trim().min(1).max(80),
    state: z
        .string()
        .trim()
        .regex(/^[A-Z]{2}$/, 'Two-letter state code'),
    postalCode: z
        .string()
        .trim()
        .regex(/^\d{5}(-\d{4})?$/, 'US ZIP code'),
    country: z.literal('US').default('US'),
});
export const createSiteRequest = z.object({
    code,
    name: displayName,
    timezone: timeZone,
    address: address.optional(),
});
export const updateSiteRequest = z
    .object({
    name: displayName,
    timezone: timeZone,
    address: address.nullable(),
    status: entityStatus,
})
    .partial();
// ---- Clients ----
export const BACKORDER_POLICIES = ['ship_partial', 'backorder', 'ship_complete'];
/** Operational settings per client (receiving tolerances, order policies). */
export const clientSettings = z.object({
    receiptOverTolerancePct: z.coerce.number().int().min(0).max(100),
    receiptShortTolerancePct: z.coerce.number().int().min(0).max(100),
    /** What happens to order lines that cannot be filled. */
    backorderPolicy: z.enum(BACKORDER_POLICIES),
    /** New orders go on hold until the operator releases the client. */
    creditHold: z.boolean(),
    /** Local cut-off time for orders that do not give one (site time zone). */
    defaultCutoff: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'HH:MM'),
});
export const createClientRequest = z
    .object({
    code,
    name: displayName,
    siteIds: z.array(uuid).max(500).default([]),
})
    .extend(clientSettings.partial().shape);
export const updateClientRequest = z
    .object({
    name: displayName,
    status: entityStatus,
    siteIds: z.array(uuid).max(500),
})
    .extend(clientSettings.shape)
    .partial();
// ---- Users ----
const scope = {
    allSites: z.boolean().default(false),
    siteIds: z.array(uuid).max(500).default([]),
    allClients: z.boolean().default(false),
    clientIds: z.array(uuid).max(1000).default([]),
};
export const createUserRequest = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('office'),
        email,
        name: displayName,
        roles: z.array(z.enum(OPERATOR_ROLES)).min(1).max(5),
        ...scope,
    }),
    z.object({
        kind: z.literal('floor'),
        name: displayName,
        badgeNo: z
            .string()
            .trim()
            .regex(/^[A-Za-z0-9-]{1,32}$/),
        roles: z.array(z.enum(OPERATOR_ROLES)).min(1).max(5),
        ...scope,
    }),
    z.object({
        kind: z.literal('client'),
        email,
        name: displayName,
        clientId: uuid,
        roles: z.array(z.enum(CLIENT_ROLES)).min(1).max(2),
    }),
]);
export const updateUserRequest = z
    .object({
    name: displayName,
    allSites: z.boolean(),
    siteIds: z.array(uuid).max(500),
    allClients: z.boolean(),
    clientIds: z.array(uuid).max(1000),
})
    .partial();
export const setUserRolesRequest = z.object({ roles: z.array(z.enum(ROLES)).min(1).max(5) });
// ---- Devices ----
export const createDeviceRequest = z.object({ siteId: uuid, name: displayName });
// ---- Audit ----
export const auditQuery = pageQuery.extend({
    entityType: z.string().max(64).optional(),
    entityId: uuid.optional(),
    actorUserId: uuid.optional(),
    from: z.iso.datetime().optional(),
    to: z.iso.datetime().optional(),
});
//# sourceMappingURL=admin.js.map